import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import {
  FileDown,
  Search,
  Package,
  AlertTriangle,
  IndianRupee,
  Building2,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_stock_summary_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

export default function StockSummaryReport() {
  const { toast } = useToast();

  // Filter states
  const [departmentId, setDepartmentId] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [stockView, setStockView] = useState("all"); // all | low

  // Filter dropdown data
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary)
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    totalStockValue: 0,
    departmentsCovered: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  // Fetch dropdown filter data (departments + categories)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
        // categories key abhi get_report_filter_data mein nahi hai —
        // backend mein CategoryMaster add karna padega, tab tak empty rahega
        setCategories(response.data.message.categories || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch stock summary data
  const fetchStockSummary = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    // IMPORTANT: hamesha append karo (empty string bhi) —
    // backend str(request.POST.get(...)) karta hai, missing hone par
    // "None" string ban ke galat filter lag jata hai
    fd.append("department_id", departmentId === "all" ? "" : departmentId);
    // Category filter ab backend pe lagta hai
    fd.append("category_id", categoryId === "all" ? "" : categoryId);
    fd.append("low_stock_only", stockView === "low" ? "true" : "false");

    try {
      const response = await server_post_data(get_stock_summary_report, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        const raw = response.data.data || [];
        const summary = response.data.summary || {};

        // Naye backend ki keys purane naam pe — table/export same rahe
        const data = raw.map((r) => ({
          ...r,
          ingredient_code: r.ingredient_alter_name ?? r.ingredient_code,
          avg_rate: r.rate ?? r.avg_rate,
          status_label:
            r.status || (r.is_low_stock ? "Low Stock" : "In Stock"),
        }));

        setStockData(data);
        setStats({
          totalItems: summary.total_items || 0,
          // low + out of stock dono "dhyan do" wale
          lowStockItems:
            (summary.low_stock_items || 0) +
            (summary.out_of_stock_items || 0),
          totalStockValue: summary.total_stock_value || 0,
          departmentsCovered: summary.departments_covered || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching stock summary:", error);
      toast({
        title: "Error",
        description: "Failed to load stock summary data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [departmentId, categoryId, stockView, toast]);

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchStockSummary();
  }, [fetchStockSummary]);

  // Excel export
  function downloadExcel(orderData, filename = "Stock_Summary_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    const formattedData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      Ingredient: item.ingredient_name || "-",
      Code: item.ingredient_code || "-",
      Category: item.category || "-",
      Department: item.department_name || "-",
      "Current Stock": formatNumber(item.current_stock),
      Unit: item.unit || "-",
      "Avg Rate (₹)": formatNumber(item.avg_rate),
      "Stock Value (₹)": formatNumber(item.stock_value),
      "Minimum Stock Alert": formatNumber(item.minimum_stock_alert),
      Status: item.status_label || (item.is_low_stock ? "Low Stock" : "In Stock"),
      "Last Updated": item.last_updated || "-",
    }));

    // Summary row
    formattedData.push({
      "Sr. No.": "",
      Ingredient: "",
      Code: "",
      Category: "",
      Department: "TOTAL",
      "Current Stock": "",
      Unit: "",
      "Avg Rate (₹)": "",
      "Stock Value (₹)": formatNumber(stats.totalStockValue),
      "Minimum Stock Alert": "",
      Status: `Low Stock Items: ${stats.lowStockItems}`,
      "Last Updated": "",
    });

    const sheet = XLSX.utils.json_to_sheet(formattedData);
    sheet["!cols"] = [
      { wch: 8 },
      { wch: 22 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
      { wch: 14 },
      { wch: 8 },
      { wch: 12 },
      { wch: 15 },
      { wch: 18 },
      { wch: 12 },
      { wch: 20 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Stock Summary");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(stockData.length / entriesPerPage);
  const currentData = stockData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Format date for table display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchStockSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Stock Summary Report"
        subtitle="Current stock position with low stock alerts"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalItems}
                </p>
                <p className="text-sm text-muted-foreground">Total Items</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.lowStockItems}
                </p>
                <p className="text-sm text-muted-foreground">Low Stock Items</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalStockValue)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Stock Value
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.departmentsCovered}
                </p>
                <p className="text-sm text-muted-foreground">
                  Departments Covered
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category — not yet filtered server-side, see fetchStockSummary note */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem
                        key={cat.primary_id}
                        value={String(cat.primary_id)}
                      >
                        {cat.category_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Stock View */}
              <div className="space-y-2">
                <Label>Stock View</Label>
                <Select value={stockView} onValueChange={setStockView}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Items" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    <SelectItem value="low">Low Stock Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(stockData)}
                  className="gap-2"
                  disabled={stockData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Ingredient
                  </TableHead>
                  <TableHead className="text-muted-foreground">Code</TableHead>
                  <TableHead className="text-muted-foreground">
                    Category
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Current Stock
                  </TableHead>
                  <TableHead className="text-muted-foreground">Unit</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Avg Rate (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Stock Value (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Min. Stock Alert
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Last Updated
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <TableRow
                      key={`${row.ingredient_id}-${row.department_id}-${index}`}
                      className={cn(
                        "border-border",
                        row.is_low_stock && "bg-destructive/5",
                      )}
                    >
                      <TableCell>
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.ingredient_name || "-"}
                      </TableCell>
                      <TableCell>{row.ingredient_code || "-"}</TableCell>
                      <TableCell>{row.category || "-"}</TableCell>
                      <TableCell>{row.department_name || "-"}</TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium",
                          row.is_low_stock && "text-destructive",
                        )}
                      >
                        {formatNumber(row.current_stock)}
                      </TableCell>
                      <TableCell>{row.unit || "-"}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.avg_rate)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatNumber(row.stock_value)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.minimum_stock_alert)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                            row.status_label === "Out of Stock"
                              ? "bg-destructive/10 text-destructive"
                              : row.status_label === "Low Stock"
                                ? "bg-warning/10 text-warning"
                                : "bg-success/10 text-success",
                          )}
                        >
                          {row.status_label}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(row.last_updated)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, stockData.length)} of{" "}
                {stockData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}